import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { reportApi, type QuarterReport } from '../lib/api'
import { dayTotals } from '../lib/db'
import { money, moneyShort } from '../lib/money'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'

const MONTH_LABEL = (m: number) => `T${m}`

export function ReportScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  const [report, setReport] = useState<QuarterReport | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'offline'>('loading')

  const today = useLiveQuery(() => dayTotals(), [], null)

  useEffect(() => {
    let alive = true
    reportApi
      .quarter()
      .then((r) => alive && (setReport(r), setState('ready')))
      .catch(() => alive && setState('offline'))
    return () => {
      alive = false
    }
  }, [])

  async function exportLedger() {
    try {
      const data = await reportApi.ledger()
      // Sổ S1-HKD là bảng phẳng — CSV mở được bằng Excel trên máy nào cũng có.
      const head = 'STT,Ngay,So hoa don,Dien giai,Doanh thu'
      const body = data.rows
        .map((r) => `${r.no},${r.date},${r.code},"${r.description}",${r.amount}`)
        .join('\n')
      const csv = `﻿${head}\n${body}\n,,,Cong,${data.total}`

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `So-doanh-thu-Q${data.quarter}-${data.year}.csv`
      a.click()
      URL.revokeObjectURL(url)
      say(`Đã tạo sổ quý ${data.quarter}`)
    } catch {
      say('Cần có mạng để lấy đủ số liệu cả quý')
    }
  }

  const taxLabel = shop?.businessType === 'Retail' ? 'Bán lẻ hàng hoá' : 'Ăn uống, dịch vụ'
  const ratePct = report ? ((report.vatRatePermille + report.pitRatePermille) / 10).toFixed(1).replace('.', ',') : '—'

  return (
    <div className="screen">
      <AppBar
        title={report ? `Quý ${report.quarter} / ${report.year}` : 'Báo cáo thuế'}
        sub={report ? `Hạn nộp ${new Date(report.dueDate).toLocaleDateString('vi-VN')}` : undefined}
        onBack={() => nav('/')}
      />

      <div className="body">
        {today && (
          <div className="card">
            <div className="pay-row">
              <span>Hôm nay tiền vô</span>
              <b className="num">{money(today.revenue)}</b>
            </div>
            <div className="pay-row">
              <span>Hôm nay tiền ra</span>
              <b className="num">{money(today.expense)}</b>
            </div>
          </div>
        )}

        {state === 'offline' && (
          <div className="card warn-card">
            <p>
              Chưa lấy được số liệu cả quý vì máy đang không có mạng. Số hôm nay ở trên vẫn đúng —
              nó nằm ngay trên máy này.
            </p>
          </div>
        )}

        {report && (
          <>
            <div className="card">
              <div className="pay-row big">
                <span>Doanh thu quý</span>
                <b className="num">{money(report.revenue)}</b>
              </div>

              <div className="bars">
                {report.months.map((m, i) => {
                  const max = Math.max(...report.months.map((x) => x.revenue), 1)
                  const isCurrent = i === report.months.length - 1
                  return (
                    <div key={m.month} className="bar-col">
                      <span className="bar-val">{moneyShort(m.revenue)}</span>
                      <div
                        className={`bar${isCurrent ? ' is-running' : ''}`}
                        style={{ height: `${Math.max(3, (m.revenue / max) * 100)}%` }}
                      />
                      <span className="bar-label">{MONTH_LABEL(m.month)}</span>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="card">
              <div className="pay-row">
                <span>
                  {taxLabel} · tỷ lệ {ratePct}%
                </span>
              </div>
              <div className="pay-row">
                <span>Thuế GTGT ({(report.vatRatePermille / 10).toFixed(1).replace('.', ',')}%)</span>
                <b className="num">{money(report.vatAmount)}</b>
              </div>
              <div className="pay-row">
                <span>Thuế TNCN ({(report.pitRatePermille / 10).toFixed(1).replace('.', ',')}%)</span>
                <b className="num">{money(report.pitAmount)}</b>
              </div>
              <div className="pay-row big divider">
                <span>Ước tính phải nộp</span>
                <b className="num">{money(report.taxTotal)}</b>
              </div>
            </div>

            <div className="card">
              <div className="pay-row">
                <span>Doanh thu năm {report.year}</span>
                <b className="num">{money(report.yearRevenue)}</b>
              </div>
              <div className="meter">
                <i
                  style={{
                    width: `${Math.min(100, (report.yearRevenue / report.taxFreeThreshold) * 100)}%`,
                  }}
                />
              </div>
              <p className="note">
                {report.overThreshold
                  ? `Đã vượt ngưỡng miễn thuế ${moneyShort(report.taxFreeThreshold)}/năm — phải kê khai theo doanh thu thực tế.`
                  : `Còn ${moneyShort(report.taxFreeThreshold - report.yearRevenue)} nữa mới chạm ngưỡng ${moneyShort(report.taxFreeThreshold)}/năm.`}
              </p>
            </div>
          </>
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-ghost grow" onClick={exportLedger}>
          Xuất sổ S1-HKD
        </button>
        <button
          type="button"
          className="btn btn-brand grow"
          onClick={() => say('Sắp có: gửi thẳng sổ quý qua Zalo cho kế toán')}
        >
          Gửi kế toán
        </button>
      </div>
    </div>
  )
}
